import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, PackageSearch } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "../../../components/ui/sonner";

// UX-FD-03 §1 SCR-P02-87 + highest-risk rule #4: stock adjustment always requires a reason
// (`StockAdjustRequest.reason`, `POST /stock/adjust`, real, audited server-side) — a delta of
// either sign is a genuine write-off/correction. Low-stock flag reuses the backend's own
// deterministic `is_low_stock` field; no automated reorder exists anywhere, so none is offered.
export default function StockAdjustment() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState({});

  const productsQ = useQuery({ queryKey: ["desk-mgr-products"], queryFn: async () => (await api.get("/products")).data.products });
  const stockQ = useQuery({
    queryKey: ["desk-mgr-stock", branchId],
    queryFn: async () => (await api.get(`/stock/${branchId}`)).data.stock,
    enabled: !!branchId,
  });

  const stockFor = (productId) => stockQ.data?.find((s) => s.product_ref === productId);
  const setDraft = (productId, field, value) => setDrafts((prev) => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));

  const adjustMutation = useMutation({
    mutationFn: ({ productId, delta, reason }) => api.post("/stock/adjust", { branch_id: branchId, product_ref: productId, delta, reason }),
    onSuccess: (_res, { productId }) => {
      toast.success("Stock adjusted.");
      setDrafts((prev) => ({ ...prev, [productId]: {} }));
      queryClient.invalidateQueries({ queryKey: ["desk-mgr-stock"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.mgrStockPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Stock adjustment</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Manual corrections require a reason — always audited.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        {(productsQ.data || []).map((p) => {
          const stock = stockFor(p.id);
          const draft = drafts[p.id] || {};
          return (
            <div key={p.id} data-testid={DESK.mgrStockRow(p.id)} className="px-4 py-3 space-y-2" style={{ borderBottom: "1px solid #F4F3EF" }}>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 font-medium" style={{ color: "#141416" }}><PackageSearch size={14} /> {p.name}</span>
                <span style={{ color: "#68686E" }}>{stock?.quantity ?? "—"} in stock</span>
              </div>
              {stock?.low_stock && (
                <p className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--scarlet-neg)" }}><AlertTriangle size={12} /> Below low-stock threshold ({stock.low_stock_threshold}) — worth considering a reorder.</p>
              )}
              <div className="flex gap-2">
                <Input
                  data-testid={DESK.mgrStockDeltaInput(p.id)} type="number" placeholder="± qty"
                  value={draft.delta || ""} onChange={(e) => setDraft(p.id, "delta", e.target.value)} className="w-24"
                />
                <Input
                  data-testid={DESK.mgrStockReasonInput(p.id)} placeholder="Reason (required)"
                  value={draft.reason || ""} onChange={(e) => setDraft(p.id, "reason", e.target.value)}
                />
                <Button
                  size="sm" data-testid={DESK.mgrStockAdjustButton(p.id)}
                  disabled={!draft.delta || !draft.reason?.trim() || adjustMutation.isPending}
                  onClick={() => adjustMutation.mutate({ productId: p.id, delta: Number(draft.delta), reason: draft.reason })}
                  className="shrink-0 text-white" style={{ background: "#141416" }}
                >
                  Adjust
                </Button>
              </div>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
}
